'use client'

export const dynamic = 'force-dynamic'

import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  HeartHandshake,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
  UserRound,
} from 'lucide-react'
import { Badge, ButtonLink, Card, StatusBadge } from '../../components/ui'
import AuthForms, { type AuthedUser } from '../../components/auth/AuthForms'
import { isPlatformAdminRole, normalizeRole } from '../../lib/auth/permissions'

/* Only honor an in-app, same-origin path as an explicit redirect target (blocks
   protocol-relative // and absolute open-redirects). */
function safeRedirect(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null
  return value
}

const roleHints = [
  { title: 'Personal account', body: 'Follow masjids, save campaigns, and get updates from your area.', icon: UserRound },
  { title: 'Committee or imam', body: 'Register or claim your masjid and manage it after verification.', icon: Building2 },
  { title: 'Volunteer or donor', body: 'Join volunteer drives, give, and see your contributions.', icon: HeartHandshake },
]

export default function LoginPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const explicitRedirect = safeRedirect(searchParams.get('redirect'))

  /* Role-aware landing: an explicit ?redirect= wins (e.g. "sign in to comment" returns to
     the feed); otherwise platform admins land on /admin and everyone else on /dashboard.
     Roles are normalized so the raw 'admin' value maps to super_admin. */
  function landAfterAuth(user?: AuthedUser) {
    if (explicitRedirect) { router.push(explicitRedirect); return }
    const role = normalizeRole(user?.role)
    router.push(isPlatformAdminRole(role) ? '/admin' : '/dashboard')
  }

  return (
    <main className="entry-page entry-page-dark">
      <div className="entry-shell">
        <div className="entry-auth-grid">
          <section className="entry-copy">
            <Link href="/" className="entry-kicker entry-kicker-light" aria-label="Back to homepage">
              <ArrowLeft size={14} />
              Masjids.in
            </Link>
            <div>
              <h1 className="entry-title entry-title-light">Welcome to Masjids.in</h1>
              <p className="entry-lede entry-lede-light">
                One account to follow masjids, give, volunteer, and — if you run a masjid — manage it.
              </p>
            </div>
            <div className="entry-signal-grid">
              <div className="entry-signal"><strong>One account</strong><span>Follow, give, volunteer, and manage — all in one place</span></div>
              <div className="entry-signal"><strong>Secure</strong><span>Your sign-in is private and protected</span></div>
              <div className="entry-signal"><strong>Free</strong><span>Creating an account costs nothing</span></div>
            </div>
            <Card tone="glass" padding="md">
              <div className="entry-card-head">
                <div>
                  <Badge tone="gold">One account</Badge>
                  <h2 className="entry-panel-title">Built for everyone</h2>
                </div>
                <LockKeyhole size={20} />
              </div>
              <p className="entry-muted">
                The same account works whether you follow a masjid, give, volunteer, or run one.
                Sign in once and it remembers you.
              </p>
            </Card>
          </section>

          <Card className="entry-card" padding="lg">
            <div className="entry-card-head">
              <div>
                <span className="entry-kicker"><ShieldCheck size={14} /> Secure access</span>
                <h2>Sign in or create your account</h2>
              </div>
              <StatusBadge tone="emerald">Free</StatusBadge>
            </div>

            <AuthForms initialMode="login" onAuthed={landAfterAuth} />

            <div className="entry-note">
              <Sparkles size={16} />
              <span>Run a masjid? Register or claim it through onboarding — committees get management access once the masjid is verified.</span>
            </div>
            <div className="entry-actions">
              <ButtonLink href="/onboard" variant="secondary" size="sm" rightIcon={<ArrowRight size={14} />}>Register a masjid</ButtonLink>
              <ButtonLink href="/masjid/claim" variant="outline" size="sm">Claim existing masjid</ButtonLink>
            </div>
          </Card>
        </div>

        <div className="entry-card-grid" style={{ marginTop: 20 }}>
          {roleHints.map((hint) => {
            const Icon = hint.icon
            return (
              <Card key={hint.title} padding="md">
                <div className="entry-list-main">
                  <span className="entry-icon-box"><Icon size={18} /></span>
                  <div>
                    <h3 className="card-title">{hint.title}</h3>
                    <p className="entry-muted">{hint.body}</p>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      </div>
    </main>
  )
}
